import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, XCircle, Clock, Upload, Plus, Trash2,
  Loader2, ChevronDown, ChevronUp, Eye, EyeOff, GripVertical,
  Edit2, X, Check, Save, RotateCcw, RefreshCw
} from 'lucide-react';
import { checklistAPI, documentsAPI } from '../../services/api';

/**
 * Widget Checklist Documents
 * @param {string} dossierId - ID du dossier (requis en mode non-admin)
 * @param {object} user - Utilisateur connecté
 * @param {function} onUpdate - Callback après mise à jour
 * @param {boolean} isAdmin - Mode administration (permet ajout/suppression/édition de types)
 */
const WidgetChecklist = ({ dossierId, user, onUpdate, isAdmin = false }) => {
  const [items, setItems] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDocType, setNewDocType] = useState({ code: '', nom: '', obligatoire: true, visible: true });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  
  // États pour l'édition et le drag & drop
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      loadDocumentTypes();
    } else if (dossierId) {
      loadChecklist();
    }
  }, [dossierId, isAdmin]);

  const loadDocumentTypes = async () => {
    try {
      setLoading(true);
      const res = await checklistAPI.getDocumentTypes();
      // Trier par ordre si disponible
      const sorted = res.data.sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
      setDocumentTypes(sorted);
    } catch (error) {
      console.error('Erreur chargement types de documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChecklist = async () => {
    try {
      setLoading(true);
      const res = await checklistAPI.getByDossier(dossierId);
      setItems(res.data);
      
      if (res.data.length === 0) {
        await checklistAPI.initForDossier(dossierId);
        const resInit = await checklistAPI.getByDossier(dossierId);
        setItems(resInit.data);
      }
    } catch (error) {
      console.error('Erreur chargement checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await checklistAPI.updateItem(
        item.id, 
        { statut: newStatus },
        user?.id,
        user ? `${user.prenom} ${user.nom}` : null
      );
      await loadChecklist();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur mise à jour:', error);
    }
  };

  const handleUpload = async (item, file) => {
    if (!file) return;
    
    setUploading(item.id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dossier_id', dossierId);
      formData.append('type_document', item.document_type_nom);
      formData.append('uploaded_by', user?.id || '');
      formData.append('uploaded_by_nom', user ? `${user.prenom} ${user.nom}` : '');
      
      const uploadRes = await documentsAPI.upload(formData);
      
      await checklistAPI.updateItem(
        item.id,
        { statut: 'recu', document_id: uploadRes.data.id },
        user?.id,
        user ? `${user.prenom} ${user.nom}` : null
      );
      
      await loadChecklist();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload');
    } finally {
      setUploading(null);
    }
  };

  // ========== FONCTIONS ADMIN ==========
  
  const handleAddDocumentType = async (e) => {
    e.preventDefault();
    if (!newDocType.code || !newDocType.nom) return;
    
    setSaving(true);
    try {
      await checklistAPI.createDocumentType({
        code: newDocType.code.toLowerCase().replace(/\s+/g, '_'),
        nom: newDocType.nom,
        obligatoire: newDocType.obligatoire,
        visible: newDocType.visible,
        categorie: 'general',
        ordre: documentTypes.length
      });
      await loadDocumentTypes();
      setNewDocType({ code: '', nom: '', obligatoire: true, visible: true });
      setShowAddForm(false);
    } catch (error) {
      console.error('Erreur création type:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocumentType = async (typeId) => {
    if (!window.confirm('Supprimer ce type de document ? Cette action est irréversible.')) return;
    
    setDeleting(typeId);
    try {
      await checklistAPI.deleteDocumentType(typeId);
      await loadDocumentTypes();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleVisibility = async (docType) => {
    setTogglingVisibility(docType.id);
    try {
      await checklistAPI.updateDocumentType(docType.id, { visible: !docType.visible });
      await loadDocumentTypes();
    } catch (error) {
      console.error('Erreur toggle visibilité:', error);
      alert('Erreur lors de la modification');
    } finally {
      setTogglingVisibility(null);
    }
  };

  // Édition inline du nom
  const startEditing = (docType) => {
    setEditingDoc(docType.id);
    setEditingName(docType.nom);
  };

  const saveEditing = async () => {
    if (!editingName.trim() || !editingDoc) return;
    
    setSaving(true);
    try {
      await checklistAPI.updateDocumentType(editingDoc, { nom: editingName.trim() });
      await loadDocumentTypes();
      setEditingDoc(null);
      setEditingName('');
    } catch (error) {
      console.error('Erreur modification nom:', error);
      alert('Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    setEditingDoc(null);
    setEditingName('');
  };

  // Drag & Drop pour réordonner
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newDocs = [...documentTypes];
    const draggedItem = newDocs[draggedIndex];
    newDocs.splice(draggedIndex, 1);
    newDocs.splice(index, 0, draggedItem);
    
    // Mettre à jour les ordres localement
    newDocs.forEach((doc, idx) => {
      doc.ordre = idx;
    });

    setDocumentTypes(newDocs);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Sauvegarder l'ordre
  const saveOrder = async () => {
    setSaving(true);
    try {
      // Sauvegarder l'ordre pour chaque document
      for (let i = 0; i < documentTypes.length; i++) {
        await checklistAPI.updateDocumentType(documentTypes[i].id, { ordre: i });
      }
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur sauvegarde ordre:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Synchroniser tous les dossiers
  const handleSyncAll = async () => {
    if (!window.confirm('Cette action va ajouter les nouveaux types de documents à tous les dossiers existants. Continuer ?')) return;
    
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await checklistAPI.syncAllDossiers();
      setSyncResult({
        success: true,
        message: res.data.message,
        details: `${res.data.dossiers_updated} dossier(s) mis à jour, ${res.data.total_items_added} document(s) ajouté(s)`
      });
    } catch (error) {
      console.error('Erreur synchronisation:', error);
      setSyncResult({
        success: false,
        message: 'Erreur lors de la synchronisation',
        details: error.response?.data?.detail || error.message
      });
    } finally {
      setSyncing(false);
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'recu': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expire': return <Clock className="w-4 h-4 text-orange-500" />;
      default: return <XCircle className="w-4 h-4 text-red-400" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  // ========== MODE ADMIN ==========
  if (isAdmin) {
    const visibleCount = documentTypes.filter(d => d.visible).length;
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-xl">📋</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Checklist Documents</h3>
                <p className="text-blue-100 text-sm">Documents requis pour les dossiers</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSyncAll}
                disabled={syncing}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm transition"
                title="Ajouter les nouveaux documents à tous les dossiers existants"
                data-testid="sync-all-btn"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Synchroniser
              </button>
              <span className="bg-white/20 px-3 py-1 rounded-full text-white text-sm">
                {visibleCount}/{documentTypes.length} visibles
              </span>
            </div>
          </div>
        </div>

        <div className="p-4">
          {/* Message de résultat de synchronisation */}
          {syncResult && (
            <div className={`mb-4 p-3 rounded-lg ${
              syncResult.success 
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="font-medium">{syncResult.message}</div>
              <div className="text-sm opacity-80">{syncResult.details}</div>
            </div>
          )}
          <p className="text-sm text-gray-600 mb-4">
            Gérez les documents requis pour les dossiers. Glissez-déposez pour réordonner. 
            Utilisez l'icône œil pour masquer/afficher un document dans la checklist.
          </p>

          {/* Liste des types de documents */}
          <div className="space-y-2 mb-4">
            {documentTypes.map((docType, index) => (
              <div 
                key={docType.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition cursor-move ${
                  draggedIndex === index
                    ? 'border-blue-400 bg-blue-50 shadow-lg'
                    : docType.visible 
                      ? 'border-green-200 bg-green-50 hover:border-green-300' 
                      : 'border-gray-200 bg-gray-100 hover:border-gray-300'
                }`}
              >
                {/* Handle drag */}
                <div className="text-gray-400 hover:text-gray-600">
                  <GripVertical className="w-5 h-5" />
                </div>

                {/* Numéro d'ordre */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  docType.visible ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  {index + 1}
                </div>

                {/* Nom du document (avec édition inline) */}
                <div className="flex-1">
                  {editingDoc === docType.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEditing();
                          if (e.key === 'Escape') cancelEditing();
                        }}
                      />
                      <button
                        onClick={saveEditing}
                        disabled={saving}
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${docType.visible ? 'text-gray-900' : 'text-gray-500'}`}>
                          {docType.nom}
                        </span>
                        {docType.obligatoire && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                            Obligatoire
                          </span>
                        )}
                        {!docType.visible && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                            Masqué
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">Code: {docType.code}</div>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1">
                  {/* Éditer */}
                  {editingDoc !== docType.id && (
                    <button
                      onClick={() => startEditing(docType)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Modifier le nom"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}

                  {/* Toggle visibilité */}
                  <button
                    onClick={() => handleToggleVisibility(docType)}
                    disabled={togglingVisibility === docType.id}
                    className={`p-2 rounded-lg transition ${
                      docType.visible 
                        ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                    title={docType.visible ? 'Masquer dans la checklist' : 'Afficher dans la checklist'}
                    data-testid={`toggle-visibility-${docType.code}`}
                  >
                    {togglingVisibility === docType.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : docType.visible ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <EyeOff className="w-5 h-5" />
                    )}
                  </button>
                  
                  {/* Supprimer */}
                  <button
                    onClick={() => handleDeleteDocumentType(docType.id)}
                    disabled={deleting === docType.id}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                    data-testid={`delete-doctype-${docType.code}`}
                  >
                    {deleting === docType.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}

            {documentTypes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>Aucun type de document configuré</p>
              </div>
            )}
          </div>

          {/* Formulaire d'ajout */}
          {showAddForm ? (
            <form onSubmit={handleAddDocumentType} className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">➕ Nouveau type de document</h4>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                  <input
                    type="text"
                    value={newDocType.code}
                    onChange={(e) => setNewDocType({ ...newDocType, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="ex: attestation_caf"
                    required
                    data-testid="input-doctype-code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Libellé *</label>
                  <input
                    type="text"
                    value={newDocType.nom}
                    onChange={(e) => setNewDocType({ ...newDocType, nom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="ex: Attestation CAF"
                    required
                    data-testid="input-doctype-nom"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="obligatoire"
                  checked={newDocType.obligatoire}
                  onChange={(e) => setNewDocType({ ...newDocType, obligatoire: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="obligatoire" className="text-sm text-gray-700">Document obligatoire</label>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="submit-doctype"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Créer
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-3 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition flex items-center justify-center gap-2 mb-4"
              data-testid="add-doctype-btn"
            >
              <Plus className="w-5 h-5" />
              Ajouter un type de document
            </button>
          )}

          {/* Bouton sauvegarder l'ordre si modifications */}
          {hasChanges && (
            <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
              <button
                onClick={saveOrder}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Enregistrer l'ordre
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  loadDocumentTypes();
                  setHasChanges(false);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                title="Annuler les modifications"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          )}

          {hasChanges && (
            <p className="text-sm text-orange-600 mt-2 text-center">
              ⚠️ Modifications d'ordre non enregistrées
            </p>
          )}
        </div>
      </div>
    );
  }

  // ========== MODE DOSSIER (UTILISATEUR) ==========
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header compact */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <h3 className="font-semibold text-gray-800">Checklist Documents</h3>
          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
            {items.filter(i => i.statut === 'recu').length}/{items.length}
          </span>
        </div>
        {collapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
      </div>

      {!collapsed && (
        <div className="p-3">
          {/* Liste des documents - LECTURE SEULE (statut + upload uniquement) */}
          <div className="space-y-1">
            {items.map((item) => (
              <div 
                key={item.id}
                className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                  item.statut === 'recu' 
                    ? 'bg-green-50' 
                    : item.statut === 'expire'
                    ? 'bg-orange-50'
                    : 'bg-gray-50'
                }`}
              >
                {/* Status icon */}
                {getStatusIcon(item.statut)}
                
                {/* Document name */}
                <span className={`flex-1 truncate ${item.statut === 'recu' ? 'text-green-700' : 'text-gray-700'}`}>
                  {item.document_type_nom}
                  {item.obligatoire && <span className="text-red-500 ml-1">*</span>}
                </span>
                
                {/* Actions (statut + upload uniquement) */}
                <div className="flex items-center gap-1">
                  {/* Status dropdown */}
                  <select
                    value={item.statut}
                    onChange={(e) => handleStatusChange(item, e.target.value)}
                    className="text-xs border rounded px-1.5 py-1 bg-white cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`status-select-${item.id}`}
                  >
                    <option value="manquant">❌ Manquant</option>
                    <option value="recu">✅ Reçu</option>
                    <option value="expire">⚠️ Expiré</option>
                  </select>
                  
                  {/* Upload */}
                  <label className="p-1.5 text-blue-600 hover:bg-blue-100 rounded cursor-pointer transition">
                    {uploading === item.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleUpload(item, e.target.files[0])}
                      disabled={uploading === item.id}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* Message informatif */}
          <p className="text-xs text-gray-400 mt-3 text-center">
            * Documents obligatoires • Gestion dans Super Admin &gt; Widgets
          </p>
        </div>
      )}
    </div>
  );
};

export default WidgetChecklist;
